// =====================================================
// COMPOSANT ShareButton
// Partage sur Harmonia (amis) + Réseaux sociaux externes
// Posts PRO : partage externe uniquement
// =====================================================
//
// PATCH i18n — Passage du texte affiché par i18next (useTranslation / t()).
// Les noms des réseaux sociaux (Facebook, WhatsApp, etc.) sont des noms
// de marque et restent codés en dur, non traduits. Icônes, couleurs, IDs
// et URLs de partage restent codés en dur, inchangés.
import React, { useState } from 'react';
import {
  View, Text, TouchableOpacity, Modal, StyleSheet,
  Platform, Linking, Share, Dimensions, ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useTranslation } from 'react-i18next';

const { width } = Dimensions.get('window');
const NATIVE    = Platform.OS !== 'web';

// ─── Types ────────────────────────────────────────────────────────────────────
interface ShareButtonProps {
  postId:           string;
  content:          string;
  isPro:            boolean;
  shared:           boolean;
  sharesCount:      number;
  onShareHarmonia:  (postId: string, shared: boolean) => Promise<void>;
}

// ─── Réseaux sociaux externes ─────────────────────────────────────────────────
interface SocialNetwork {
  name:    string;
  icon:    string;
  color:   string;
  buildUrl: (text: string) => string;
}

const SOCIAL_NETWORKS: SocialNetwork[] = [
  {
    name:     'Facebook',
    icon:     'logo-facebook',
    color:    '#1877F2',
    buildUrl: (text) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://harmonia.app')}&quote=${encodeURIComponent(text)}`,
  },
  {
    name:     'Twitter / X',
    icon:     'logo-twitter',
    color:    '#000000',
    buildUrl: (text) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
  },
  {
    name:     'WhatsApp',
    icon:     'logo-whatsapp',
    color:    '#25D366',
    buildUrl: (text) => `https://wa.me/?text=${encodeURIComponent(text)}`,
  },
  {
    name:     'Telegram',
    icon:     'send',
    color:    '#0088CC',
    buildUrl: (text) => `https://t.me/share/url?url=${encodeURIComponent('https://harmonia.app')}&text=${encodeURIComponent(text)}`,
  },
  {
    name:     'Instagram',
    icon:     'logo-instagram',
    color:    '#E1306C',
    buildUrl: (_text) => `https://www.instagram.com/`,
  },
  {
    name:     'TikTok',
    icon:     'logo-tiktok', // Mise à jour : icône TikTok officielle
    color:    '#010101',
    buildUrl: (_text) => `https://www.tiktok.com/`,
  },
  {
    name:     'Pinterest',
    icon:     'logo-pinterest',
    color:    '#E60023',
    buildUrl: (text) => `https://pinterest.com/pin/create/button/?description=${encodeURIComponent(text)}`,
  },
  {
    name:     'LinkedIn',
    icon:     'logo-linkedin',
    color:    '#0A66C2',
    buildUrl: (text) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://harmonia.app')}&summary=${encodeURIComponent(text)}`,
  },
  {
    name:     'YouTube',
    icon:     'logo-youtube',
    color:    '#FF0000',
    buildUrl: (_text) => `https://www.youtube.com/`,
  },
  {
    name:     'Snapchat',
    icon:     'happy-outline',
    color:    '#FFFC00',
    buildUrl: (text) => `https://www.snapchat.com/scan?attachmentUrl=${encodeURIComponent('https://harmonia.app')}`,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const openUrl = async (url: string) => {
  try {
    if (Platform.OS === 'web') {
      window.open(url, '_blank');
    } else {
      const supported = await Linking.canOpenURL(url);
      if (supported) await Linking.openURL(url);
      else await Linking.openURL(url); // fallback web browser
    }
  } catch { }
};

const shareNative = async (content: string) => {
  try {
    if (Platform.OS === 'web') {
      if (navigator.share) {
        await navigator.share({
          title: 'Harmonia',
          text:  content,
          url:   'https://harmonia.app',
        });
      }
    } else {
      await Share.share({
        message: `${content}\n\nhttps://www.harmoniaworld.world`,
        title:   'Harmonia',
      });
    }
  } catch { }
};

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────────
export default function ShareButton({
  postId, content, isPro, shared, sharesCount, onShareHarmonia,
}: ShareButtonProps) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [loading,   setLoading]   = useState(false);

  const handleOpen = () => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setShowModal(true);
  };

  const handleShareHarmonia = async () => {
    if (loading) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setLoading(true);
    try {
      await onShareHarmonia(postId, !shared);
    } finally {
      setLoading(false);
      setShowModal(false);
    }
  };

  const handleSocial = async (network: SocialNetwork) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const url = network.buildUrl(content);
    setShowModal(false);
    await openUrl(url);
  };

  const handleMore = async () => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setShowModal(false);
    await shareNative(content);
  };

  return (
    <>
      {/* ── BOUTON DÉCLENCHEUR ─────────────────────────────────────────── */}
      <TouchableOpacity
        style={btnStyles.trigger}
        onPress={handleOpen}
        activeOpacity={0.6}
      >
        <View style={btnStyles.triggerContent}>
          <Ionicons
            name={shared && !isPro ? 'repeat' : 'share-social-outline'}
            size={24}
            color={shared && !isPro ? '#10B981' : '#65676B'}
          />
          {shared && !isPro && sharesCount > 0 && (
            <Text style={btnStyles.sharesCount}>{sharesCount}</Text>
          )}
        </View>
      </TouchableOpacity>

      {/* ── MODAL PARTAGE ─────────────────────────────────────────────── */}
      <Modal
        visible={showModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowModal(false)}
      >
        <TouchableOpacity
          style={btnStyles.overlay}
          activeOpacity={1}
          onPress={() => setShowModal(false)}
        >
          <View style={btnStyles.sheet}>
            {/* Handle */}
            <View style={btnStyles.handle} />

            <Text style={btnStyles.sheetTitle}>{t('shareButton.title')}</Text>

            {/* ── PARTAGER SUR HARMONIA (posts non-PRO uniquement) ───── */}
            {!isPro && (
              <TouchableOpacity
                style={[btnStyles.harmoniaBtn, shared && btnStyles.harmoniaBtnShared]}
                onPress={handleShareHarmonia}
                disabled={loading}
                activeOpacity={0.8}
              >
                <View style={[btnStyles.harmoniaIcon, shared && btnStyles.harmoniaIconShared]}>
                  <Ionicons name={shared ? 'repeat' : 'repeat-outline'} size={22} color={shared ? '#fff' : '#8A2BE2'} />
                </View>
                <View style={btnStyles.harmoniaText}>
                  <Text style={[btnStyles.harmoniaTitle, shared && { color: '#fff' }]}>
                    {shared ? t('shareButton.sharedOnHarmonia') : t('shareButton.shareWithFriends')}
                  </Text>
                  <Text style={[btnStyles.harmoniaSubtitle, shared && { color: 'rgba(255,255,255,0.75)' }]}>
                    {t('shareButton.visibleInFriendsFeed')}
                  </Text>
                </View>
              </TouchableOpacity>
            )}

            {/* ── SÉPARATEUR ────────────────────────────────────────── */}
            <View style={btnStyles.separator}>
              <View style={btnStyles.separatorLine} />
              <Text style={btnStyles.separatorText}>{t('shareButton.socialNetworks')}</Text>
              <View style={btnStyles.separatorLine} />
            </View>

            {/* ── GRILLE RÉSEAUX SOCIAUX ────────────────────────────── */}
            <ScrollView
              horizontal={false}
              showsVerticalScrollIndicator={false}
              style={btnStyles.socialScroll}
            >
              <View style={btnStyles.socialGrid}>
                {SOCIAL_NETWORKS.map(network => (
                  <TouchableOpacity
                    key={network.name}
                    style={btnStyles.socialItem}
                    onPress={() => handleSocial(network)}
                    activeOpacity={0.7}
                  >
                    <View style={[btnStyles.socialIcon, { backgroundColor: network.color }]}>
                      <Ionicons name={network.icon as any} size={22} color="#fff" />
                    </View>
                    <Text style={btnStyles.socialName} numberOfLines={1}>{network.name}</Text>
                  </TouchableOpacity>
                ))}

                {/* ── BOUTON PLUS (partage natif système) ─────────── */}
                <TouchableOpacity
                  style={btnStyles.socialItem}
                  onPress={handleMore}
                  activeOpacity={0.7}
                >
                  <View style={[btnStyles.socialIcon, { backgroundColor: '#8A2BE2' }]}>
                    <Ionicons name="ellipsis-horizontal" size={22} color="#fff" />
                  </View>
                  <Text style={btnStyles.socialName}>{t('shareButton.more')}</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>

            {/* ── ANNULER ───────────────────────────────────────────── */}
            <TouchableOpacity
              style={btnStyles.cancelBtn}
              onPress={() => setShowModal(false)}
              activeOpacity={0.8}
            >
              <Text style={btnStyles.cancelText}>{t('shareButton.cancel')}</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const btnStyles = StyleSheet.create({
  // Bouton déclencheur
  trigger:        { flex: 1, paddingVertical: 8, borderRadius: 8 },
  triggerContent: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5 },
  sharesCount:    { fontSize: 13, fontWeight: '600', color: '#10B981' },

  // Modal sheet
  overlay:     { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sheet:       { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, paddingBottom: Platform.OS === 'ios' ? 40 : 24, paddingHorizontal: 20, paddingTop: 12, maxHeight: '85%' },
  handle:      { width: 40, height: 4, backgroundColor: '#E0E0E0', borderRadius: 2, alignSelf: 'center', marginBottom: 16 },
  sheetTitle:  { fontSize: 18, fontWeight: '700', color: '#1A1A1A', textAlign: 'center', marginBottom: 20 },

  // Harmonia share
  harmoniaBtn:        { flexDirection: 'row', alignItems: 'center', gap: 14, backgroundColor: '#F0E6FF', borderRadius: 16, padding: 16, marginBottom: 20, borderWidth: 1.5, borderColor: '#D8B4FE' },
  harmoniaBtnShared:  { backgroundColor: '#8A2BE2', borderColor: '#8A2BE2' },
  harmoniaIcon:       { width: 44, height: 44, borderRadius: 22, backgroundColor: '#EDE9FE', justifyContent: 'center', alignItems: 'center' },
  harmoniaIconShared: { backgroundColor: 'rgba(255,255,255,0.25)' },
  harmoniaText:       { flex: 1 },
  harmoniaTitle:      { fontSize: 15, fontWeight: '700', color: '#4B0082', marginBottom: 2 },
  harmoniaSubtitle:   { fontSize: 12, color: '#7C3AED' },

  // Séparateur
  separator:      { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 16 },
  separatorLine:  { flex: 1, height: 1, backgroundColor: '#E5E7EB' },
  separatorText:  { fontSize: 12, color: '#9CA3AF', fontWeight: '600' },

  // Grille réseaux
  socialScroll: { maxHeight: 280 },
  socialGrid:   { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'flex-start', gap: 8, paddingBottom: 8 },
  socialItem:   { width: (width - 40 - 32) / 5, alignItems: 'center', gap: 6 },
  socialIcon:   { width: 48, height: 48, borderRadius: 14, justifyContent: 'center', alignItems: 'center' },
  socialName:   { fontSize: 10, color: '#555', fontWeight: '600', textAlign: 'center' },

  // Annuler
  cancelBtn:  { marginTop: 12, backgroundColor: '#F3F4F6', borderRadius: 14, paddingVertical: 14, alignItems: 'center' },
  cancelText: { fontSize: 15, fontWeight: '700', color: '#374151' },
});
